import React, { useEffect, useState } from 'react';
import { Box, TextField, MenuItem, Button, Alert, CircularProgress, Grid, Typography } from '@mui/material';
import { buildHeightOptions } from '../../utils/heightUtils';
import { listStates, listCountiesForState, listDistrictsForCounty } from '../../services/schoolDistrictService';
import { GRADES } from '../../utils/grades';

const HEIGHT_OPTIONS = buildHeightOptions();

/**
 * Shared athlete form — first/last name, DOB, grade, school/home-program
 * context, and (create only) an optional initial height/weight. Used by
 * both Create Athlete and Edit Athlete Profile.
 *
 * includeMeasurements controls whether height/weight show at all:
 * - Create Athlete (true): height/weight are a one-time INITIAL
 *   measurement, logged as the athlete's first athlete_measurements row.
 * - Edit Athlete Profile (false): height/weight are intentionally absent
 *   here. They're growth history now, not a profile field to overwrite —
 *   see the separate "Current Measurements" section on the Athlete
 *   Profile page, which appends a new dated measurement instead of
 *   editing this record. Showing them in both places, with two different
 *   save semantics (overwrite vs. append) behind what looks like the same
 *   text field, is exactly the confusion this split avoids.
 *
 * State/County/School District/ZIP (011) show in BOTH contexts, unlike
 * height/weight — they're ordinary profile fields, correctable anytime.
 * State/County/School District are all STRICT, database-backed dropdowns
 * (states -> counties -> school_districts, cascaded via the
 * school_district_counties many-to-many join) — not free text. The form
 * submits stateCode/countyId/schoolDistrictId (canonical references), not
 * names; AthleteProfilePage reads the canonical names back via a join,
 * not from anything this form stores directly. School District is never
 * inferred from County or ZIP — a family can live in one community and
 * attend/play in a neighboring district, so it's always its own explicit
 * choice, and a district that spans multiple counties (a real, common
 * case in Wisconsin — see migration 011) is offered under every county
 * it's linked to. Changing State clears County + District; changing
 * County clears District — a selection from a level above always
 * invalidates whatever was picked below it.
 */
export default function AthleteForm({ initialValues, onSubmit, submitLabel, onCancel, saving, error, includeMeasurements = false }) {
  const [form, setForm] = useState({
    firstName: initialValues?.firstName || '',
    lastName: initialValues?.lastName || '',
    dateOfBirth: initialValues?.dateOfBirth || '',
    grade: initialValues?.grade || '',
    heightIn: initialValues?.heightIn ?? '',
    weightLb: initialValues?.weightLb ?? '',
    stateCode: initialValues?.stateCode || '',
    countyId: initialValues?.countyId || '',
    schoolDistrictId: initialValues?.schoolDistrictId || '',
    zipCode: initialValues?.zipCode || '',
  });
  const [localError, setLocalError] = useState(null);
  const [stateOptions, setStateOptions] = useState([]);
  const [countyOptions, setCountyOptions] = useState([]);
  const [districtOptions, setDistrictOptions] = useState([]);

  const set = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  useEffect(() => {
    listStates().then(setStateOptions).catch(() => {});
  }, []);

  useEffect(() => {
    let cancelled = false;
    if (!form.stateCode) { setCountyOptions([]); return; }
    listCountiesForState(form.stateCode).then(opts => { if (!cancelled) setCountyOptions(opts); }).catch(() => {});
    return () => { cancelled = true; };
  }, [form.stateCode]);

  useEffect(() => {
    let cancelled = false;
    if (!form.countyId) { setDistrictOptions([]); return; }
    listDistrictsForCounty(form.countyId).then(opts => { if (!cancelled) setDistrictOptions(opts); }).catch(() => {});
    return () => { cancelled = true; };
  }, [form.countyId]);

  const handleStateChange = (e) => {
    const stateCode = e.target.value;
    setForm(f => ({ ...f, stateCode, countyId: '', schoolDistrictId: '' }));
  };
  const handleCountyChange = (e) => {
    const countyId = e.target.value;
    setForm(f => ({ ...f, countyId, schoolDistrictId: '' }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError(null);
    if (!form.firstName || !form.lastName || !form.dateOfBirth || !form.grade) {
      setLocalError('First name, last name, date of birth, and grade are required.');
      return;
    }
    if (form.zipCode && !/^\d{5}(-\d{4})?$/.test(form.zipCode.trim())) {
      setLocalError('ZIP code should look like 12345 or 12345-6789.');
      return;
    }
    onSubmit({
      firstName: form.firstName,
      lastName: form.lastName,
      dateOfBirth: form.dateOfBirth,
      grade: form.grade,
      stateCode: form.stateCode || null,
      countyId: form.countyId || null,
      schoolDistrictId: form.schoolDistrictId || null,
      zipCode: form.zipCode.trim() || null,
      ...(includeMeasurements ? {
        heightIn: form.heightIn === '' ? null : Number(form.heightIn),
        weightLb: form.weightLb === '' ? null : Number(form.weightLb),
      } : {}),
    });
  };

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="First name" value={form.firstName} onChange={set('firstName')} required />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth label="Last name" value={form.lastName} onChange={set('lastName')} required />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth type="date" label="Date of birth" value={form.dateOfBirth} onChange={set('dateOfBirth')}
            InputLabelProps={{ shrink: true }} inputProps={{ max: new Date().toISOString().slice(0, 10) }} required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField fullWidth select label="Grade" value={form.grade} onChange={set('grade')} required sx={{ minWidth: 160 }}>
            {GRADES.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: '#6a8aa8', letterSpacing: '0.06em', mt: 1 }}>
            SCHOOL / HOME PROGRAM (OPTIONAL)
          </Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth select label="State" value={form.stateCode} onChange={handleStateChange}
            helperText=" " sx={{ minWidth: 140 }}
          >
            <MenuItem value="">Not set</MenuItem>
            {stateOptions.map(s => <MenuItem key={s.code} value={s.code}>{s.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth select label="County" value={form.countyId} onChange={handleCountyChange}
            disabled={!form.stateCode} helperText={form.stateCode ? ' ' : 'Select a state first'} sx={{ minWidth: 160 }}
          >
            <MenuItem value="">Not set</MenuItem>
            {countyOptions.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth label="ZIP Code" value={form.zipCode} onChange={set('zipCode')}
            placeholder="12345" helperText="Optional" inputProps={{ maxLength: 10 }}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth select label="School District" value={form.schoolDistrictId} onChange={set('schoolDistrictId')}
            disabled={!form.countyId}
            helperText={
              !form.countyId
                ? 'Select a county first'
                : districtOptions.length === 0
                  ? "No districts loaded for this county yet — this athlete's real district may not be in our data yet."
                  : 'Not inferred from county or ZIP — select the district this athlete actually attends/plays in'
            }
          >
            <MenuItem value="">Not set</MenuItem>
            {districtOptions.map(d => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          </TextField>
        </Grid>

        {includeMeasurements && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth select label="Height" value={form.heightIn} onChange={set('heightIn')}
                helperText="Optional — can add later" sx={{ minWidth: 160 }}
              >
                <MenuItem value="">Not set</MenuItem>
                {HEIGHT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth type="number" label="Weight (lb)" value={form.weightLb} onChange={set('weightLb')}
                inputProps={{ min: 0, step: 0.1 }} helperText="Optional — can add later"
              />
            </Grid>
          </>
        )}
      </Grid>

      {(localError || error) && <Alert severity="error" sx={{ mt: 3 }}>{localError || error}</Alert>}

      <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
        <Button
          type="submit" variant="contained" color="primary" disabled={saving}
          size="large"
          sx={{ textTransform: 'none', fontWeight: 800, px: 4 }}
        >
          {saving ? <CircularProgress size={20} sx={{ color: '#050c16' }} /> : submitLabel}
        </Button>
        {onCancel && (
          <Button onClick={onCancel} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
            Cancel
          </Button>
        )}
      </Box>
    </Box>
  );
}
